/**
 * Хелперы для работы с событиями
 * 
 * Аналог хелперов с вебсайта для единообразного отображения
 */

import type { Event, RecurrenceType, EventStatus } from '../model/types'

/**
 * Вычисление актуального статуса события по дате и повторяемости
 */
export function getComputedStatus(event: Event): EventStatus {
  // Черновик и отменённые — не меняем
  if (event.status === 'draft') return 'draft'
  if (event.status === 'cancelled') return 'cancelled'
  
  const now = new Date()
  const starts = event.startAt ? new Date(event.startAt) : null
  
  // События без даты — оставляем текущий статус
  if (!starts) {
    if (event.status === 'scheduled' || event.status === 'ongoing') {
      return event.status
    }
    return 'draft'
  }
  
  // Повторяющееся событие — особая логика
  const isRecurring = event.recurrenceType && event.recurrenceType !== 'none'
  
  // Ещё не началось
  if (now < starts) return 'scheduled'
  
  // Вычисляем дату окончания
  let ends = event.endAt ? new Date(event.endAt) : null
  if (!ends && event.durationMinutes) {
    ends = new Date(starts.getTime() + event.durationMinutes * 60 * 1000)
  }
  
  // Повторяющееся событие — всегда активно
  if (isRecurring) {
    return 'scheduled'
  }
  
  // Для НЕповторяющихся: завершено если прошло окончание
  if (ends && now > ends) return 'completed'
  
  return 'ongoing'
}

/**
 * Проверка — активное ли событие
 */
export function isEventActive(event: Event): boolean {
  const status = getComputedStatus(event)
  return status === 'scheduled' || status === 'ongoing'
}

/**
 * Умное форматирование даты в зависимости от повторяемости
 * - Еженедельно → "По субботам"
 * - Раз в 2 недели → "По субботам"
 * - Ежемесячно → "Каждое 15-е число"
 * - Ежедневно → "Каждый день"
 * - Одноразовое → "5 декабря"
 */
export function getEventDate(event: Event): string {
  if (!event.startAt) return ''
  
  const date = new Date(event.startAt)
  const recurrence = event.recurrenceType
  
  // Повторяющиеся события — показываем паттерн
  if (recurrence && recurrence !== 'none') {
    if (recurrence === 'daily') {
      return 'Каждый день'
    }
    
    if (recurrence === 'weekly' || recurrence === 'biweekly') {
      const dayNames: Record<number, string> = {
        0: 'воскресеньям',
        1: 'понедельникам',
        2: 'вторникам',
        3: 'средам',
        4: 'четвергам',
        5: 'пятницам',
        6: 'субботам',
      }
      return `По ${dayNames[date.getDay()]}`
    }
    
    if (recurrence === 'monthly') {
      const day = date.getDate()
      return `Каждое ${day}-е число`
    }
  }
  
  // Одноразовое событие — обычная дата
  return date.toLocaleDateString('ru-RU', { 
    day: 'numeric', 
    month: 'long'
  })
}

/**
 * Форматирование времени события
 */
export function getEventTime(event: Event): string {
  if (!event.startAt) return ''
  return new Date(event.startAt).toLocaleTimeString('ru-RU', { 
    hour: '2-digit', 
    minute: '2-digit' 
  })
}

/**
 * Получение лейбла для типа повторения
 */
export function getRecurrenceLabel(type?: RecurrenceType): string | null {
  if (!type || type === 'none') return null
  const labels: Record<RecurrenceType, string> = {
    none: '',
    daily: 'Ежедневно',
    weekly: 'Еженедельно',
    biweekly: 'Раз в 2 недели',
    monthly: 'Ежемесячно',
  }
  return labels[type]
}

/**
 * Форматирование цены события
 */
export function getEventPrice(event: Event): string {
  if (event.isFree || !event.price || event.price === 0) return 'Бесплатно'
  return `${event.price.toLocaleString('ru-RU')} ₽`
}

/**
 * Получение оставшихся мест
 */
export function getSpotsLeft(event: Event): number | null {
  if (!event.maxParticipants) return null
  const current = event.currentParticipants || 0
  return Math.max(0, event.maxParticipants - current)
}

/**
 * Проверка, является ли событие повторяющимся
 */
export function isRecurringEvent(event: Event): boolean {
  return !!event.recurrenceType && event.recurrenceType !== 'none'
}

/**
 * Проверка, есть ли у события дата
 */
export function hasEventDate(event: Event): boolean {
  return !!event.startAt
}

